import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { cssVar } from '../theme/variables.js';
import { useInView } from 'framer-motion';
import { css, styled } from 'styled-components';
import { preventForwardPropsConfig } from '../utils/preventForwardProps.js';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { mediaMobile } from '../constants/styled-utils.js';
import React__default from 'react';
import { useScrollPosition } from '@fabric-app/hooks/useScrollPosition';
import { mergeRefs } from 'react-merge-refs';

const ScrollAreaFadeDirection = {
  vertical: css([
    `&::after,&::before{bottom:0;left:0;right:0;height:1.25rem;background:linear-gradient(180deg,var(--scroll-area-gradient-steps));}&::before{bottom:unset;top:0;background:linear-gradient(360deg,var(--scroll-area-gradient-steps));}[data-scroll-vertical='n/a'] &{&::before,&::after{opacity:0;}}[data-scroll-vertical='top'] &{&::before{opacity:0;}}[data-scroll-vertical='bottom'] &{&::after{opacity:0;}}`
  ]),
  horizontal: css([
    `&::after,&::before{top:0;bottom:0;width:1.25rem;background:linear-gradient(90deg,var(--scroll-area-gradient-steps));}&::before{left:0;right:unset;background:linear-gradient(270deg,var(--scroll-area-gradient-steps));}[data-scroll-horizontal='n/a'] &{&::before,&::after{opacity:0;}}[data-scroll-horizontal='left'] &{&::before{opacity:0;}}[data-scroll-horizontal='right'] &{&::after{opacity:0;}}`
  ])
};
const ScrollAreaFade = styled.div.withConfig({
  displayName: "ScrollArea__ScrollAreaFade",
  componentId: "sc-14200cdd-0"
})([
  `position:absolute;z-index:1;pointer-events:none;inset:0;`,
  ` `,
  ` &::after,&::before{content:'';position:absolute;opacity:1;transition:opacity 0.2s ease-out;}`,
  ` `,
  ``
], ({ fadeColor = "color-bg-secondary" }) => css([
  `--scroll-area-gradient-steps:rgba(`,
  `,0) 0%,var(--scroll-area-fade-bg-color,`,
  `) 100%;`
], cssVar[`${fadeColor}-rgb`], cssVar[fadeColor]), ({ orientation }) => ScrollAreaFadeDirection[orientation], ({ fade }) => fade === "end" ? css([
  `&::before{display:none;}`
]) : "", ({ fade }) => fade === "start" ? css([
  `&::after{display:none;}`
]) : "");
const ScrollAreaRoot = styled(ScrollAreaPrimitive.Root).withConfig(preventForwardPropsConfig([
  "grow",
  "asFlex"
])).withConfig({
  displayName: "ScrollArea__ScrollAreaRoot",
  componentId: "sc-14200cdd-1"
})([
  `position:relative;overflow:hidden;--scrollbar-size:10px;--scrollbar-bg-color:`,
  `;--scrollbar-thumb-color:hsla(`,
  `,0.4);`,
  `;`,
  `;`
], cssVar["color-bg-tertiary"], cssVar["color-bg-octonary-hsl"], ({ asFlex }) => asFlex && css([
  `display:flex;flex-direction:column;`
]), ({ grow }) => grow && "flex: 1");
const useInViewWithinRadixScrollArea = (ref, options) => {
  const radixViewport = ref.current?.closest("[data-radix-scroll-area-viewport]") || null;
  const refEl = React__default.useRef(radixViewport);
  refEl.current = radixViewport;
  const inView = useInView(ref, {
    ...options,
    root: options?.root || refEl
  });
  return inView;
};
const ScrollAreaViewport = styled(ScrollAreaPrimitive.Viewport).withConfig(preventForwardPropsConfig([
  "expandVertically",
  "innerDisplayFlex",
  "verticallyAlignToCenter"
])).withConfig({
  displayName: "ScrollArea__ScrollAreaViewport",
  componentId: "sc-14200cdd-2"
})([
  `width:100%;height:100%;border-radius:inherit;`,
  `;`,
  ``
], ({ expandVertically }) => expandVertically && css([
  `> div{min-height:100%;height:100%;}`
]), ({ innerDisplayFlex, verticallyAlignToCenter }) => innerDisplayFlex && css([
  `display:block;& > div{min-height:100%;display:flex !important;flex-direction:column;`,
  `}`
], verticallyAlignToCenter && `justify-content: center;`));
const ScrollAreaCorner = ScrollAreaPrimitive.Corner;
const scrollbarVariantsCss = ({ variant = "default" }) => {
  switch (variant) {
    case "subtle":
      return css([
        `--scrollbar-bg-color:transparent;--scrollbar-thumb-color:hsla(`,
        `,0.2);`
      ], cssVar["color-bg-octonary-hsl"]);
    case "none":
      return css([
        `display:none;`
      ]);
    default:
      return "";
  }
};
const ScrollAreaScrollbar = styled(ScrollAreaPrimitive.ScrollAreaScrollbar).withConfig({
  displayName: "ScrollArea__ScrollAreaScrollbar",
  componentId: "sc-14200cdd-3"
})([
  `display:flex;touch-action:none;user-select:none;background-color:var(--scrollbar-bg-color);z-index:30;`,
  ` `,
  ` `,
  ` @media (pointer:coarse){--scrollbar-bg-color:transparent;}`,
  `{--scrollbar-bg-color:transparent;}`
], ({ orientation }) => orientation === "vertical" && `
    width: var(--scrollbar-size);
    height: 100%;
    border-left: 1px solid transparent;
    padding: 2px;
  `, ({ orientation }) => orientation === "horizontal" && `
    height: var(--scrollbar-size);
    flex-direction: column;
    border-top: 1px solid transparent;
    padding: 2px;
  `, scrollbarVariantsCss, mediaMobile);
const ScrollAreaThumb = styled(ScrollAreaPrimitive.ScrollAreaThumb).withConfig({
  displayName: "ScrollArea__ScrollAreaThumb",
  componentId: "sc-14200cdd-4"
})([
  `position:relative;flex:1;border-radius:9999px;background-color:var(--scrollbar-thumb-color);`
]);
const ScrollAreaDefault = /* @__PURE__ */ React__default.forwardRef(({ children, orientation = "vertical", expandVertically, innerDisplayFlex, verticallyAlignToCenter, scrollbarVariant, fade, fadeColor, viewportRef: viewportRefProp, ...props }, ref) => {
  const viewportRef = React__default.useRef(null);
  const viewportRefMerged = React__default.useMemo(() => mergeRefs([
    viewportRef,
    viewportRefProp ?? null
  ]), [
    viewportRef,
    viewportRefProp
  ]);
  const [scrollPosition] = useScrollPosition(viewportRef.current);
  return /* @__PURE__ */ jsxs(ScrollAreaRoot, {
    ref,
    ...props,
    "data-scroll-vertical": scrollPosition?.vertical,
    "data-scroll-horizontal": scrollPosition?.horizontal,
    children: [
      /* @__PURE__ */ jsx(ScrollAreaViewport, {
        expandVertically,
        innerDisplayFlex,
        verticallyAlignToCenter,
        ref: viewportRefMerged,
        children
      }),
      orientation === "vertical" || orientation === "both" ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(ScrollAreaScrollbar, {
            orientation: "vertical",
            variant: scrollbarVariant,
            children: /* @__PURE__ */ jsx(ScrollAreaThumb, {})
          }),
          fade && /* @__PURE__ */ jsx(ScrollAreaFade, {
            fade,
            "data-scrollbar-fade-area": true,
            orientation: "vertical",
            fadeColor
          })
        ]
      }) : null,
      orientation === "horizontal" || orientation === "both" ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(ScrollAreaScrollbar, {
            orientation: "horizontal",
            variant: scrollbarVariant,
            children: /* @__PURE__ */ jsx(ScrollAreaThumb, {})
          }),
          fade && /* @__PURE__ */ jsx(ScrollAreaFade, {
            fade,
            "data-scrollbar-fade-area": true,
            orientation: "horizontal",
            fadeColor
          })
        ]
      }) : null,
      /* @__PURE__ */ jsx(ScrollAreaCorner, {})
    ]
  });
});
ScrollAreaDefault.displayName = ScrollAreaPrimitive.Root.displayName;
const ScrollArea = {
  Default: ScrollAreaDefault,
  Root: ScrollAreaRoot,
  Viewport: ScrollAreaViewport,
  Corner: ScrollAreaCorner,
  Bar: ScrollAreaScrollbar,
  Thumb: ScrollAreaThumb
};

export { ScrollArea, ScrollAreaCorner, ScrollAreaDefault, ScrollAreaFade, ScrollAreaRoot, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport, useInViewWithinRadixScrollArea };
//# sourceMappingURL=ScrollArea.js.map
